/**
 * FacetsSidebar component renders a sidebar with various product facets (filters) for a product listing page (PLP).
 * It conditionally displays different types of filters based on the attributes passed through the `facets` prop.
 * - Price filter is rendered as a range slider if the selected price attributes are different.
 * - Colour filter is handled by a custom refinement list component to display color options.
 * - For other attributes, a general refinement list component (`CustomRefinementList`) is used to display available options.
 *
 * The component is optimized by using `useMemo` to transform and sort facet items, preventing unnecessary recalculations on each render.
 * The display of certain attributes is controlled by the `attributeMapping` to ensure the correct display labels are used,
 * and unnecessary or unwanted attributes (like "recently viewed" or "brands") are filtered out.
 *
 * @param {object} props - The component props.
 * @param {string[]} props.facets - List of facet attributes to be displayed in the sidebar.
 * @returns {TSX.Element} - A sidebar with dynamic facet filters.
 */
import React, { useMemo, useState } from 'react';
import CustomRefinementList from './CustomRefinementList';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { attributeMapping } from 'algolia/product-list';
import { ColourRefinementList } from 'helpers/utils/useRefinementList';
import { RangeSlider } from '../../product-list-algolia/components/facets/rangeSlider';
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { getHexCode } from 'helpers/utils/getHexcode';
import { Color, KeyValue } from '..';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import { PRICE_KEYS } from 'helpers/constants/algolia';
type FacetsSidebarProps = {
  facets: string[];
  colors: Color[];
  renderingContent?: any;
  handleTrackFacetClick?: (facetType: string, value: string) => void;
  keyLabelMap: KeyValue;
};

function FacetsSidebar({ facets, colors, renderingContent, handleTrackFacetClick, keyLabelMap }: FacetsSidebarProps) {
  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string = localeSelection?.currency || null;
  const priceAttributes = currency === 'GBP' ? PRICE_KEYS?.GB : PRICE_KEYS?.EU;

  const [lowPrice, setLowPrice] = useState<number | undefined>(0);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(10);

  return (
    <>
      {facets?.map((attribute) => {
        if (attribute === 'variants.attributes.colour') {
          return null;
        }
        const attributeLabel = keyLabelMap?.[attribute] ?? attributeMapping?.[attribute];
        if (!attributeLabel) return null;

        const hasFacetValues = facets && Object.keys(facets).length > 0;
        if (!hasFacetValues) return null;
        if (attribute === priceAttributes && lowPrice !== maxPrice) {
          return (
            <div key={attribute} className="border-0 border-neutral-400 lg:border-b">
              <AccordionBtn
                variant="plusAndMinus"
                className="text-16 font-bold leading-7"
                closedSectionTitle="PRICE RANGE"
                buttonClassName="pt-15 font-bold text-16 leading-tight text-primary-black border-neutral-400"
                panelClassName="pb-15"
                defaultOpen={true}
                sectionTileClassName="font-body text-16 font-bold uppercase leading-[28px]"
              >
                <div className="p-4 py-10">
                  <RangeSlider attribute={priceAttributes} handleTrackFacetClick={handleTrackFacetClick} />
                </div>
              </AccordionBtn>
            </div>
          );
        }

        if (attribute === 'colour') {
          return (
            <ColourRefinementList
              key={attribute}
              attribute="colour"
              colors={colors}
              handleTrackFacetClick={handleTrackFacetClick}
            >
              {(content) => (
                <AccordionBtn
                  variant="plusAndMinus"
                  className="text-16 font-bold leading-7"
                  closedSectionTitle="COLOUR"
                  buttonClassName="pt-15 font-bold text-16 leading-tight text-primary-black border-neutral-400"
                  panelClassName="pb-15"
                  defaultOpen={true}
                  sectionTileClassName="font-body text-16 font-bold uppercase leading-[28px]"
                >
                  {content}
                </AccordionBtn>
              )}
            </ColourRefinementList>
          );
        }
        return (
          <CustomRefinementList
            key={attribute}
            attribute={attribute}
            handleTrackFacetClick={handleTrackFacetClick}
            facetOrdering={renderingContent?.facetOrdering || {}}
            showMoreLimit={1000}
            showFirstLimit={10}
          >
            {(content) => (
              <AccordionBtn
                variant="plusAndMinus"
                className="font-body text-16 font-bold uppercase leading-[28px]"
                closedSectionTitle={keyLabelMap?.[attribute] ?? attributeMapping?.[attribute]}
                sectionTileClassName="font-body text-16 font-bold uppercase leading-[28px]"
                buttonClassName="py-15 font-bold text-16 leading-[28px] font-body uppercase"
                panelClassName="pb-15"
                defaultOpen={true}
              >
                {content}
              </AccordionBtn>
            )}
          </CustomRefinementList>
        );
      })}
    </>
  );
}
export default FacetsSidebar;
